import { api, ApiError } from "@/lib/api/client";
import { startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { CeremonyCanceledError, guardedCeremony, type CeremonyOwner } from "@/lib/webauthn";
import type { components } from "@/types/api";

export type StepUpProof = components["schemas"]["StepUpProof"];
type StepUpChallengeResponse = components["schemas"]["StepUpChallengeResponse"];

const alwaysStart = () => true;

const proofGuards = new WeakMap<StepUpProof, () => boolean>();
const proofSentListeners = new WeakMap<StepUpProof, () => void>();

export class StepUpAbandonedError extends Error {
  constructor() {
    super("The step-up was dismissed before its proof was sent.");
    this.name = "AbortError";
  }
}

export function bindStepUpProof(
  proof: StepUpProof,
  stillWanted: () => boolean,
  onSent?: () => void,
): void {
  proofGuards.set(proof, stillWanted);
  if (onSent) proofSentListeners.set(proof, onSent);
  else proofSentListeners.delete(proof);
}

export function assertStepUpProofWanted(
  proof: StepUpProof | undefined,
  { sendsProof = true }: { sendsProof?: boolean } = {},
): void {
  if (!proof) return;
  if (proofGuards.get(proof)?.() === false) {
    throw new StepUpAbandonedError();
  }
  if (!sendsProof) return;
  const onSent = proofSentListeners.get(proof);
  if (onSent) {
    proofSentListeners.delete(proof);
    onSent();
  }
}

export async function assertPasskeyStepUp(
  owner: CeremonyOwner,
  shouldStart: () => boolean = alwaysStart,
): Promise<StepUpProof> {
  const { options } = await api.post<StepUpChallengeResponse>(
    "/v1/mfa/stepup/challenge",
    undefined,
    { retrySafe: true },
  );
  if (!shouldStart()) {
    throw new CeremonyCanceledError("The passkey prompt was abandoned before it opened.");
  }
  const response = await guardedCeremony(
    () =>
      startAuthentication({
        optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
      }),
    { owner },
  );
  return { type: "passkey", response: response as unknown as StepUpProof["response"] };
}

export function totpStepUp(code: string): StepUpProof {
  return { type: "totp", code };
}

export function isStepUpRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401 && err.code === "step_up_required";
}

export function isStepUpUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === "step_up_unavailable";
}
